// @ts-check

class BodyTypePage extends HTMLElement {
  constructor() {
    super();

    this.config = {
      useState: this.hasAttribute("use-state"),
    };

    /** @type {{[key: string]: string}} */
    this.BODY_TYPE_DISPLAY_VALUES = {
      0: "WIDER",
      "chest-wider": "WIDER",
      1: "SAME",
      "chest-same": "SAME",
      2: "NARROWER",
      "chest-narrower": "NARROWER",
    };

    this.refs = {
      /** @type {() => NodeListOf<HTMLInputElement>} */
      bodyTypeSelectors: () =>
        this.querySelectorAll("input[data-body-type-input"),
      /** @type {() => HTMLDivElement | null} */
      shoulderWidthImage: () => this.querySelector("#shoulder-image"),
      /** @type {() => HTMLDivElement | null} */
      shoulderWidthImageBadge: () =>
        this.querySelector("#shoulder-image-badge"),
    };

    /** @type {Record<'bodyType', string>} */
    this.giftInitialValues = {
      bodyType: "chest-same",
    };

    /** @type {Partial<Record<'bodyType', string>>} */
    this.defaultValues = {
      bodyType:
        this.getAttribute("initial-body-type") ??
        this.giftInitialValues.bodyType,
    };

    /** @type {Map<'bodyType', string>} */
    this.currentValues = new Map();

    /** @type {PromiseWithResolvers<boolean>} */
    this.readyPromise = Promise.withResolvers();
  }

  connectedCallback() {
    if (this.config.useState) {
      this.setDefaultValues();
    }

    this.setBodyTypeValue(this.defaultValues.bodyType);
    this.assignBodyTypeListeners();
    this.readyPromise.resolve(true);
  }

  disconnectedCallback() {
    this.readyPromise.reject();
  }

  /**
   * @param {'bodyType'} attr
   * @param {string} value
   */
  updateState(attr, value) {
    if (this.config.useState) {
      this.currentValues.set(attr, value);
      updateURLSearchParams({ [attr]: value });
    }
  }

  /** @param {'bodyType'} attr */
  resetState(attr) {
    if (this.config.useState) {
      this.currentValues.delete(attr);
      updateURLSearchParams({ [attr]: undefined });
    }
  }

  /** @param {string | undefined} value */
  setBodyTypeValue(value) {
    let isSet = false;
    const selectors = this.refs.bodyTypeSelectors();
    selectors.forEach((selector) => {
      if (value === selector.value) {
        this.onBodyTypeChange(selector);
        isSet = true;
      }
    });
    if (!isSet) {
      selectors.forEach((selector) => {
        selector.checked = false;
      });
      this.resetState("bodyType");
      this.setBodyTypeImage();
    }
  }

  /** @param {HTMLInputElement} selector */
  onBodyTypeChange(selector) {
    switch (selector.value) {
      case "0":
      case "chest-wider": {
        selector.checked = true;
        this.updateState("bodyType", selector.value);
        this.setBodyTypeImage("body_type_wider.svg", false);
        break;
      }
      case "1":
      case "chest-same": {
        selector.checked = true;
        this.updateState("bodyType", selector.value);
        this.setBodyTypeImage("body_type_same.svg", true);
        break;
      }
      case "2":
      case "chest-narrower": {
        selector.checked = true;
        this.updateState("bodyType", selector.value);
        this.setBodyTypeImage("body_type_narrower.svg", false);
        break;
      }
      default: {
        selector.checked = false;
        this.resetState("bodyType");
        this.setBodyTypeImage();
      }
    }
  }

  assignBodyTypeListeners() {
    const selectors = this.refs.bodyTypeSelectors();
    selectors.forEach((s) => {
      s.addEventListener("change", () => this.onBodyTypeChange(s));
    });
  }

  /**
   * @param {'body_type_wider.svg' | 'body_type_same.svg' | 'body_type_narrower.svg'} [filename]
   * @param {boolean} [showBadge]
   */
  setBodyTypeImage(filename = "body_type_same.svg", showBadge = true) {
    const imageContainer = this.refs.shoulderWidthImage();
    const imageBadgeContainer = this.refs.shoulderWidthImageBadge();
    if (!imageContainer || !imageBadgeContainer) {
      throw new Error("Image or image badge container not found");
    }
    imageContainer.style.backgroundImage = `url(${fileUrl}${filename})`;
    if (showBadge) {
      show(imageBadgeContainer);
    } else {
      hide(imageBadgeContainer);
    }
  }

  setDefaultValues() {
    const savedValues = getInitialValues(["bodyType"]);
    if (savedValues) {
      this.defaultValues = savedValues;
    }
  }

  /** @param {Record<'bodyType', string | undefined>} obj */
  async setValues({ bodyType }) {
    await this.readyPromise.promise;
    this.setBodyTypeValue(bodyType);
  }

  async getValues() {
    await this.readyPromise.promise;
    return this.currentValues;
  }

  async resetValues() {
    await this.readyPromise.promise;
    await this.setValues(this.giftInitialValues);
  }

  /** @param {string | undefined} value */
  validateBodyTypeValue(value) {
    if (!value) {
      return false;
    }
    return this.BODY_TYPE_DISPLAY_VALUES.hasOwnProperty(value);
  }

  /** @param {string} value */
  displayBodyType(value) {
    return this.BODY_TYPE_DISPLAY_VALUES[value];
  }
}

if (!customElements.get("body-type-page")) {
  customElements.define("body-type-page", BodyTypePage);
}
